import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type TrailerLocationStatus = Database["public"]["Enums"]["trailer_location_status"];
type TrailerEventType = Database["public"]["Enums"]["trailer_event_type"];

/**
 * EAPI reported the trailer at a yard and nobody has checked it in yet
 * ("pending check-in", Trailer Arrival Design). Physically at the yard, so it
 * counts in yard counts and lists, but NOT checked in: order confirmation and
 * the trailer pickers still require `at_site`.
 *
 * TODO(types): drop the cast once src/integrations/supabase/types.ts is
 * regenerated with migration 20261015200000 ('arrived' in the enum).
 */
export const TRAILER_ARRIVED_STATUS = "arrived" as unknown as TrailerLocationStatus;

/** Logged at check-in when the operator confirms they inspected an arrived trailer. Same TODO(types). */
export const TRAILER_INSPECTED_EVENT = "inspected" as unknown as TrailerEventType;

/** True when EAPI reported the trailer at a yard and it has not been checked in yet. */
export function isTrailerArrived(
  trailer: { current_location_status?: string | null } | null | undefined,
): boolean {
  return trailer?.current_location_status === "arrived";
}

interface TrailerRecord {
  id: string;
}

/** A trailer is considered "on site" when YMS check-in has set its current
 *  location to a known yard. `current_location_status === "at_site"` together
 *  with a non-null `current_location_id` is the canonical signal used by the
 *  Yard Site view and is what gates order confirmation. */
export function isTrailerOnSite(
  trailer:
    | { current_location_status?: string | null; current_location_id?: string | null }
    | null
    | undefined,
): boolean {
  if (!trailer) return false;
  return (
    trailer.current_location_status === "at_site" &&
    !!trailer.current_location_id
  );
}

export interface CheckInMetadata {
  truckId?: string;
  truckOwner?: "aurora" | "partner";
  sealId?: string;
  loadStatus?: string;
  notes?: string;
  scac?: string;
  isCustomerParking?: boolean;
  isFoundTrailer?: boolean;
  sealStatus?: "intact" | "broken";
  /**
   * AYMS "Needs Service" flag. When true, the trailer is blocked from
   * order assignment in the Edit Order form until cleared on a later
   * check-in. A reason is required when true.
   */
  needsService?: boolean;
  needsServiceReason?: string;
  /**
   * Trailer type chosen on the check-in form. Only applied when the existing
   * trailer record has no type yet — check-in never overwrites a known type.
   */
  trailerTypeId?: string;
  /**
   * The operator ticked "I have inspected the trailer" (required when checking
   * in an arrived trailer). Logged as an `inspected` trailer event next to the
   * `checked_in` one.
   */
  inspected?: boolean;
}

/**
 * Look up a trailer by its human-readable trailer_id, insert a checked_in event
 * with optional metadata, and update the trailer to at_site at the given location.
 * Returns the trailer DB record and the event id, or null if the trailer doesn't exist.
 */
export async function checkInTrailer(
  trailerId: string,
  locationId: string,
  userId?: string,
  meta?: CheckInMetadata,
  tenantId?: string
): Promise<{ trailer: TrailerRecord; eventId: string } | null> {
  // trailer_events.tenant_id defaults to Dev (see checkOutTrailer): an
  // inspection logged without a tenant would land in the wrong tenant.
  if (meta?.inspected && !tenantId) {
    throw new Error("checkInTrailer: an inspected check-in requires a tenantId");
  }
  let trailerQuery = supabase
    .from("trailers")
    .select("id, trailer_type_id")
    .eq("trailer_id", trailerId.trim());
  if (tenantId) trailerQuery = trailerQuery.eq("tenant_id", tenantId);
  const { data: trailer } = await trailerQuery.maybeSingle();

  if (!trailer) return null;

  const eventPayload: any = {
    trailer_id: trailer.id,
    event_type: "checked_in" as const,
    location_id: locationId,
    created_by_user_id: userId ?? null,
    truck_id: meta?.truckId || null,
    seal_id: meta?.sealId || null,
    load_status: meta?.loadStatus || null,
    seal_status: meta?.sealStatus || null,
    notes: meta?.isFoundTrailer ? "Found during yard audit" : (meta?.notes || null),
    needs_service: meta?.needsService ?? false,
    needs_service_reason: meta?.needsService ? (meta?.needsServiceReason?.trim() || null) : null,
  };
  if (tenantId) eventPayload.tenant_id = tenantId;

  const { data: evt } = await supabase.from("trailer_events").insert(eventPayload).select("id").single();

  const updatePayload: any = {
    current_location_status: "at_site" as const,
    current_location_id: locationId,
    seal_id: meta?.sealId || null,
    seal_status: meta?.sealStatus || null,
    scac: meta?.scac || undefined,
    is_customer_parking: meta?.isCustomerParking ?? false,
    needs_service: meta?.needsService ?? false,
    needs_service_reason: meta?.needsService ? (meta?.needsServiceReason?.trim() || null) : null,
    needs_service_set_at: meta?.needsService ? new Date().toISOString() : null,
    needs_service_set_by: meta?.needsService ? (userId ?? null) : null,
  };
  // Never default the load status to "unknown": when the caller didn't
  // capture one (order-driven check-in), leave the trailer's last known
  // value in place instead of downgrading it.
  if (meta?.loadStatus) updatePayload.load_status = meta.loadStatus;
  // Backfill a missing trailer type from the check-in form, but never
  // overwrite a type the trailer already has.
  if (meta?.trailerTypeId && !(trailer as any).trailer_type_id) {
    updatePayload.trailer_type_id = meta.trailerTypeId;
  }

  const { error: updateError } = await supabase
    .from("trailers")
    .update(updatePayload)
    .eq("id", trailer.id);

  // Log the inspection only once the trailer is actually checked in, so the
  // audit trail never says "inspected" for a check-in that didn't happen.
  if (meta?.inspected) {
    if (updateError) throw new Error(`Check-in failed: ${updateError.message}`);
    const { error: inspectedError } = await supabase.from("trailer_events").insert({
      trailer_id: trailer.id,
      event_type: TRAILER_INSPECTED_EVENT,
      location_id: locationId,
      created_by_user_id: userId ?? null,
      tenant_id: tenantId,
    });
    if (inspectedError) throw new Error(`Logging the inspection failed: ${inspectedError.message}`);
  }

  return { trailer, eventId: evt?.id ?? "" };
}

/**
 * Look up a trailer by its human-readable trailer_id, insert a checked_out event,
 * and update the trailer to in_transit with null location.
 * Returns the trailer DB record or null if the trailer doesn't exist.
 */
export async function checkOutTrailer(
  trailerId: string,
  locationId: string,
  userId?: string,
  tenantId?: string,
  meta?: { sealStatus?: "intact" | "broken" }
): Promise<{ trailer: TrailerRecord; eventId: string } | null> {
  // `trailer_events.tenant_id` carries a column default (Dev). Inserting
  // without an explicit tenant silently files a Production event under the
  // Dev tenant, corrupting tenant-scoped event history — fail loudly instead.
  if (!tenantId) {
    throw new Error("checkOutTrailer requires a tenantId");
  }
  let trailerQuery = supabase
    .from("trailers")
    .select("id")
    .eq("trailer_id", trailerId.trim());
  if (tenantId) trailerQuery = trailerQuery.eq("tenant_id", tenantId);
  const { data: trailer } = await trailerQuery.maybeSingle();

  if (!trailer) return null;

  const checkoutPayload: any = {
    trailer_id: trailer.id,
    event_type: "checked_out" as const,
    location_id: locationId,
    created_by_user_id: userId ?? null,
    seal_status: meta?.sealStatus || null,
  };
  if (tenantId) checkoutPayload.tenant_id = tenantId;

  const { data: evt } = await supabase
    .from("trailer_events")
    .insert(checkoutPayload)
    .select("id")
    .single();

  await supabase
    .from("trailers")
    .update({
      current_location_status: "in_transit" as const,
      current_location_id: null,
      seal_status: meta?.sealStatus || null,
    })
    .eq("id", trailer.id);

  return { trailer, eventId: evt?.id ?? "" };
}
